<template>
  <div class="channels-page">
    <!-- 草稿续写 banner：有未发布的 AI 会话草稿时提示继续（对齐 client/ home_page 的 draft banner） -->
    <div v-if="showDraftBanner" class="draft-banner" @click="goDraft">
      <div class="draft-banner-icon">
        <van-icon name="edit" size="16" color="#fff" />
      </div>
      <div class="draft-banner-texts">
        <div class="draft-banner-title">{{ $t('home_draftBannerTitle') }}</div>
        <div class="draft-banner-subtitle">{{ $t('home_draftBannerSubtitle') }}</div>
      </div>
      <van-icon name="arrow" size="14" color="#8A8A8A" />
      <van-icon name="cross" size="14" color="#8A8A8A" style="margin-left: 10px;" @click.stop="dismissDraftBanner" />
    </div>
    <!-- 顶部 tab 切换 + 操作按钮 -->
    <div class="channels-top-bar">
      <div class="channels-tabs">
        <div class="channels-tab" :class="{ active: activeTab === 'my' }" @click="activeTab = 'my'">
          {{ $t('channels_myChannels') }}
        </div>
        <div class="channels-tab" :class="{ active: activeTab === 'discover' }" @click="activeTab = 'discover'">
          {{ $t('channels_discover') }}
        </div>
      </div>
      <div class="channels-top-actions">
        <div class="channels-icon-btn" @click="openSearch">
          <van-icon name="search" size="20" />
        </div>
        <div class="channels-icon-btn primary" @click="showJoinByCode = true">
          <van-icon name="plus" size="20" />
        </div>
      </div>
    </div>

    <!-- 列表 -->
    <div class="channels-list" ref="channelsList">
      <van-pull-refresh v-if="activeTab === 'my'" v-model="myRefreshing" :loading-text="$t('loading')" @refresh="onMyRefresh">
        <div v-if="myLoading && displayChannels.length === 0" style="display: flex; justify-content: center; padding: 40px;">
          <van-loading size="24" />
        </div>

        <div v-for="channel in displayChannels" :key="channel.channelId" class="channel-card"
          @click="goToChannelDetail(channel)">
          <!-- 头像 -->
          <div class="channel-card-avatar">
            <img v-if="channel.avatar" :src="$common.getFullImageUrl(channel.avatar, $config)" class="channel-avatar-img" />
            <div v-else class="channel-avatar-placeholder">
              {{ (channel.name || '#')[0] }}
            </div>
          </div>
          <!-- 信息 -->
          <div class="channel-card-info">
            <div class="channel-card-name">{{ channel.name }}</div>
            <div v-if="channel.description" class="channel-card-desc">{{ channel.description }}</div>
            <div class="channel-card-meta">
              <van-icon name="friends-o" size="13" color="#bbb" />
              <span>{{ channel.memberCnt || 0 }}</span>
            </div>
            <!-- lastSession 预览 -->
            <div v-if="channel.lastSession && !isPrivateSessionForViewer(channel.lastSession) && ((channel.lastSession.userMsg || channel.lastSession.aiSummary) || channel.lastSession.fromUserName)" class="channel-last-session">
              <van-icon name="chat-o" size="12" color="#8A8A8A" />
              <span v-if="channel.lastSession.userMsg || channel.lastSession.aiSummary" class="channel-last-session-summary">{{ $common.formatUserMsgPreview(channel.lastSession.userMsg) || channel.lastSession.aiSummary }}</span>
              <span v-if="channel.lastSession.fromUserName" class="channel-last-session-user" :style="!(channel.lastSession.userMsg || channel.lastSession.aiSummary) ? 'margin-left: auto;' : ''">
                <img v-if="channel.lastSession.fromUserAvatar"
                  :src="$common.getFullImageUrl(channel.lastSession.fromUserAvatar, $config, 's')"
                  class="channel-last-session-avatar" />
                <span v-else class="channel-last-session-avatar-placeholder">
                  {{ (channel.lastSession.fromUserName || '?')[0] }}
                </span>
                <span class="channel-last-session-user-name">{{ channel.lastSession.fromUserName }}</span>
              </span>
            </div>
            <!-- lastSession 为私密会话：展示私密提示 -->
            <div v-else-if="channel.lastSession && isPrivateSessionForViewer(channel.lastSession)" class="channel-last-session">
              <van-icon name="lock" size="12" color="#8A8A8A" />
              <span>{{ $t('match_private_hint') }}</span>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="displayChannels.length === 0 && !myLoading && myHasLoaded" class="my-hubs-empty">
          <van-icon name="friends-o" size="56" color="#ddd" />
          <div class="my-hubs-empty-title">{{ $store.state.alreadyLogin ? $t('channels_noMyGroupsTitle') : $t('channels_loginPrompt') }}</div>
          <div v-if="$store.state.alreadyLogin" class="my-hubs-empty-subtitle">{{ $t('channels_noMyGroupsSubtitle') }}</div>
          <van-button
            v-if="$store.state.alreadyLogin"
            type="primary"
            color="#E8543A"
            round
            class="my-hubs-empty-btn"
            @click="openSearch('hubs')"
          >
            <van-icon name="search" size="16" style="margin-right: 4px;" />
            {{ $t('channels_searchGroupsBtn') }}
          </van-button>
        </div>

        <!-- 加载更多指示器 -->
        <div v-if="myLoading && displayChannels.length > 0" style="text-align: center; padding: 16px;">
          <van-loading size="20" />
        </div>
      </van-pull-refresh>

      <!-- Discover 区域（含 Feed / Hubs 子切换） -->
      <div v-if="activeTab === 'discover'">
        <!-- Feed / Hubs 分段切换 -->
        <div class="discover-sub-tabs">
          <div class="discover-sub-tab" :class="{ active: discoverSubTab === 'hubs' }" @click="switchDiscoverSubTab('hubs')">
            {{ $t('channels_groups') }}
          </div>
          <div class="discover-sub-tab" :class="{ active: discoverSubTab === 'feed' }" @click="switchDiscoverSubTab('feed')">
            {{ $t('channels_feed') }}
          </div>
        </div>

        <!-- Feed 子视图 -->
        <van-pull-refresh v-if="discoverSubTab === 'feed'" v-model="feedRefreshing" :loading-text="$t('loading')" @refresh="onFeedRefresh">
          <van-list :loading="feedLoading" :loading-text="$t('loading')" :finished="!feedHasMore" :finished-text="feedList.length > 0 ? '' : ''" @load="loadMoreFeed" :immediate-check="false" :offset="50">
          <!-- Feed 卡片列表 -->
          <div v-for="session in feedList" :key="session.chatSessionId" class="feed-card"
            @click="openSessionDetail(session)">
            <div class="feed-card-header">
              <div class="feed-card-user-top">
                <img v-if="session.fromUserAvatar" :src="$common.getFullImageUrl(session.fromUserAvatar, $config, 's')" class="feed-card-avatar-top" />
                <div v-else class="feed-card-avatar-top-placeholder">{{ (session.fromUserName || '?')[0] }}</div>
                <span class="feed-card-username-top">{{ session.fromUserName }}</span>
              </div>
              <span class="feed-card-time">{{ $common.formatSmartTime(session.createdAt, $i18n.locale) }}</span>
            </div>
            <div v-if="session.userMsg" class="feed-card-msg">{{ $common.formatUserMsgPreview(session.userMsg) }}</div>
            <div v-if="session.aiSummary" class="feed-card-summary">
              <span class="feed-card-ai-tag">{{ $t('aiChat_matchSummaryLabel') }}</span>
              <span class="feed-card-ai-text">{{ session.aiSummary }}</span>
            </div>
            <!-- Match 按钮 -->
            <div v-if="session.matchCnt > 0" class="feed-card-match" @click.stop="viewMatchHistory(session.chatSessionId)">
              <span v-if="session.public == 1 && session.matchPaused != 1" class="match-live-icon">
                <span class="match-live-ripple"></span>
                <span class="match-live-ripple match-live-ripple2"></span>
                <van-icon name="friends-o" size="12" color="#2A8FAD" class="match-live-core" />
              </span>
              <van-icon v-else-if="String(session.fromUserId) === myUserId && session.matchPaused == 1" name="pause-circle-o" size="12" color="#8A8A8A" />
              <van-icon v-else name="friends-o" size="12" color="#2A8FAD" />
              <span :style="String(session.fromUserId) === myUserId && session.matchPaused == 1 ? 'color:#8A8A8A' : ''">{{ $tc('chatSessionMatch_count', session.matchCnt, {n: session.matchCnt}) }}</span>
            </div>
            <!-- 媒体预览 -->
            <div v-if="parseFirstMediaContent(session.firstMediaContent)" class="feed-card-media">
              <template v-if="parseFirstMediaContent(session.firstMediaContent).type === 'img'">
                <img :src="$common.getFullImageUrl(parseFirstMediaContent(session.firstMediaContent).content.url || '', $config)" class="feed-card-media-img" />
              </template>
              <template v-else-if="parseFirstMediaContent(session.firstMediaContent).type === 'pick_location'">
                <div class="feed-card-media-location">
                  <img :src="getStaticMapUrl(parseFirstMediaContent(session.firstMediaContent).content.lat, parseFirstMediaContent(session.firstMediaContent).content.lng)" class="feed-card-media-map" />
                  <div class="feed-card-media-location-info">
                    <van-icon name="location-o" size="10" color="#3DAECF" />
                    <span>{{ parseFirstMediaContent(session.firstMediaContent).content.address || '' }}</span>
                  </div>
                </div>
              </template>
              <template v-else-if="parseFirstMediaContent(session.firstMediaContent).type === 'file'">
                <van-icon name="description" size="12" color="#8A8A8A" />
                <span class="feed-card-media-text">{{ parseFirstMediaContent(session.firstMediaContent).content.name || parseFirstMediaContent(session.firstMediaContent).content.fileName || '' }}</span>
              </template>
            </div>
            <!-- 评论预览 -->
            <div v-if="parseCommentPreviews(session.latestCommentsJson).length > 0" class="feed-card-comments" @click.stop>
              <div v-if="!isFeedCommentExpanded(session.chatSessionId)" @click.stop="toggleFeedCommentExpand(session.chatSessionId)">
                <div v-if="session.chatSessionCommentCnt > 2" class="feed-card-comment-more">
                  <van-icon name="arrow-down" size="12" color="#8A8A8A" />
                  <span>{{ $t('chatSessionComment_viewEarlier', {n: session.chatSessionCommentCnt - Math.min(2, parseCommentPreviews(session.latestCommentsJson).length)}) }}</span>
                </div>
                <div v-for="(preview, pIdx) in [...parseCommentPreviews(session.latestCommentsJson)].reverse().slice(0, 2)" :key="pIdx" class="feed-card-comment-item">
                  <img v-if="preview.userAvatar" :src="$common.getFullImageUrl(preview.userAvatar, $config, 's')" class="feed-card-comment-avatar" />
                  <div v-else class="feed-card-comment-avatar-default">{{ (preview.userName || 'A')[0] }}</div>
                  <div class="feed-card-comment-body">
                    <div class="feed-card-comment-name">{{ preview.userName || $t('anonymous') }}</div>
                    <div class="feed-card-comment-content"><span v-if="preview.replyToUserName" class="feed-card-comment-reply-to">@{{ preview.replyToUserName }} </span>{{ preview.content }}</div>
                  </div>
                </div>
              </div>
              <div v-else>
                <div v-for="(preview, pIdx) in [...parseCommentPreviews(session.latestCommentsJson)].reverse()" :key="pIdx" class="feed-card-comment-item">
                  <img v-if="preview.userAvatar" :src="$common.getFullImageUrl(preview.userAvatar, $config, 's')" class="feed-card-comment-avatar" />
                  <div v-else class="feed-card-comment-avatar-default">{{ (preview.userName || 'A')[0] }}</div>
                  <div class="feed-card-comment-body">
                    <div class="feed-card-comment-name">{{ preview.userName || $t('anonymous') }}</div>
                    <div class="feed-card-comment-content"><span v-if="preview.replyToUserName" class="feed-card-comment-reply-to">@{{ preview.replyToUserName }} </span>{{ preview.content }}</div>
                  </div>
                </div>
                <div class="feed-card-comment-more" @click.stop="toggleFeedCommentExpand(session.chatSessionId)">
                  <van-icon name="arrow-up" size="12" color="#8A8A8A" />
                  <span>{{ $t('cancel') }}</span>
                </div>
              </div>
            </div>
            <div class="feed-card-footer">
              <div v-if="session.channelName" class="feed-card-channel-hub">
                <img v-if="session.channelAvatar" :src="$common.getFullImageUrl(session.channelAvatar, $config)" class="feed-card-channel-avatar-img" />
                <div v-else class="feed-card-channel-avatar">{{ session.channelName[0] }}</div>
                <span class="feed-card-channel">{{ session.channelName }}</span>
              </div>
              <div class="feed-card-actions">
                <span class="feed-card-go" @click.stop="openChannelInNewTab(session)">
                  <van-icon name="chat-o" size="11" color="#FFFFFF" style="margin-right: 3px;" />{{ $t('feed_goToGroup') }}
                </span>
              </div>
            </div>
          </div>
          </van-list>
          <van-empty v-if="feedList.length === 0 && !feedLoading && feedHasLoaded" :description="$t('feed_empty')" />
        </van-pull-refresh>

        <!-- Hubs 子视图 -->
        <van-pull-refresh v-if="discoverSubTab === 'hubs'" v-model="discoverRefreshing" :loading-text="$t('loading')" @refresh="onDiscoverRefresh">
          <div v-if="discoverLoading && discoverChannels.length === 0" style="display: flex; justify-content: center; padding: 40px;">
            <van-loading size="24" />
          </div>

          <div v-for="channel in discoverChannels" :key="channel.channelId" class="channel-card"
            @click="goToChannelDetail(channel)">
            <div class="channel-card-avatar">
              <img v-if="channel.avatar" :src="$common.getFullImageUrl(channel.avatar, $config)" class="channel-avatar-img" />
              <div v-else class="channel-avatar-placeholder">
                {{ (channel.name || '#')[0] }}
              </div>
            </div>
            <div class="channel-card-info">
              <div class="channel-card-name">{{ channel.name }}</div>
              <div v-if="channel.description" class="channel-card-desc">{{ channel.description }}</div>
              <div class="channel-card-meta">
                <span style="flex-shrink: 0;">{{ channel.memberCnt || 0 }} {{ $t('channel_detail_members') }}</span>
                <!-- 最新消息弱化展示：与成员数同一行，浅灰 chip + 蓝色图标，与 description 区分，不显示发送人 -->
                <span v-if="!isPrivateSessionForViewer(channel.lastSession) && channel.lastSession && (channel.lastSession.userMsg || channel.lastSession.aiSummary)" class="channel-card-lastmsg">
                  <van-icon name="chat-o" size="12" color="#3DAECF" />
                  <span class="channel-card-lastmsg-text">{{ $common.formatUserMsgPreview(channel.lastSession.userMsg) || channel.lastSession.aiSummary }}</span>
                </span>
                <span v-else-if="isPrivateSessionForViewer(channel.lastSession)" class="channel-card-lastmsg">
                  <van-icon name="lock" size="12" color="#3DAECF" />
                  <span class="channel-card-lastmsg-text">{{ $t('match_private_hint') }}</span>
                </span>
              </div>
            </div>
          </div>

          <van-empty v-if="discoverChannels.length === 0 && !discoverLoading && discoverHasLoaded"
            :description="$t('channels_noChannels')" />
          <div v-if="discoverLoading && discoverChannels.length > 0" style="text-align: center; padding: 16px;">
            <van-loading size="20" />
          </div>
        </van-pull-refresh>
      </div>
    </div>

    <!-- 群码加入弹窗 -->
    <van-popup v-model="showJoinByCode" round position="bottom" :style="{ height: '50%' }">
      <div class="join-by-code-popup">
        <div class="join-by-code-header">
          <span style="font-weight: 600;">{{ $t('channels_joinByCodeTitle') }}</span>
          <van-icon name="cross" size="20" color="#666" @click="showJoinByCode = false" />
        </div>
        <div style="padding: 20px;">
          <div style="text-align: center; margin-bottom: 20px;">
            <div style="width: 56px; height: 56px; border-radius: 16px; background: #F7F5F2; display: flex; align-items: center; justify-content: center; margin: 0 auto;">
              <span style="font-size: 28px; font-weight: bold; color: #1A1A1A;">#</span>
            </div>
          </div>
          <input
            v-model="joinCode"
            type="text"
            maxlength="8"
            :placeholder="$t('channels_joinByCodeHint')"
            style="width: 100%; padding: 16px; font-size: 24px; font-weight: 700; text-align: center; letter-spacing: 6px; border: none; background: #F7F5F2; border-radius: 14px; outline: none;"
            @input="joinCode = joinCode.toUpperCase()"
          />
          <div v-if="joinCodeError" style="color: #E8543A; font-size: 13px; text-align: center; margin-top: 8px;">
            {{ joinCodeError }}
          </div>
          <van-button type="primary" block color="#E8543A" style="margin-top: 20px;" :loading="joiningByCode"
            :disabled="!joinCode.trim() || joiningByCode" @click="joinByCode">
            {{ $t('channels_joinByCodeConfirm') }}
          </van-button>
        </div>
      </div>
    </van-popup>

    <!-- 扫码加群弹窗（Web端无法扫码，引导用户输入群码） -->
    <van-popup v-model="showJoinByQR" round position="bottom" :style="{ height: '50%' }">
      <div class="join-by-code-popup">
        <div class="join-by-code-header">
          <span style="font-weight: 600;">{{ $t('channels_joinByQRTitle') }}</span>
          <van-icon name="cross" size="20" color="#666" @click="showJoinByQR = false" />
        </div>
        <div style="padding: 20px; text-align: center;">
          <div style="width: 56px; height: 56px; border-radius: 16px; background: #F7F5F2; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
            <van-icon name="qr" size="28" color="#333" />
          </div>
          <p style="font-size: 14px; color: #8A8A8A; line-height: 1.6;">
            {{ $t('channels_joinByQRWebHint') }}
          </p>
          <van-button type="primary" block color="#E8543A" style="margin-top: 20px;" @click="openQRScanner">
            {{ $t('channels_joinByCode') }}
          </van-button>
        </div>
      </div>
    </van-popup>

    <!-- Action Sheet -->
    <van-action-sheet
      v-model="showActionSheet"
      :actions="actionSheetActions"
      cancel-text=" "
      close-on-click-action
    />

    <!-- 搜索弹窗（带 Hubs/Chats/My 三 tab） -->
    <van-popup v-model="showSearchPopup" round position="bottom" :style="{ height: '85%' }" :lazy-render="false">
      <div class="search-popup">
        <div class="search-popup-header">
          <div class="search-popup-search-inner">
            <van-icon name="search" size="14" color="#999" />
            <input
              v-model="searchPopupQuery"
              type="text"
              :placeholder="searchPopupTab === 'chats' ? $t('channels_searchHintChats') : searchPopupTab === 'hubs' ? $t('channels_searchHintGroups') : $t('channels_searchHintMy')"
              style="flex: 1; border: none; background: transparent; padding: 8px; font-size: 14px; outline: none;"
              @input="onSearchPopupInput"
            />
            <van-icon v-if="searchPopupQuery" name="clear" size="14" color="#999" style="cursor: pointer;"
              @click="clearSearchPopup" />
          </div>
          <span style="color: #8A8A8A; font-size: 14px; cursor: pointer; margin-left: 12px;" @click="showSearchPopup = false">
            {{ $t('cancel') }}
          </span>
        </div>

        <!-- 搜索 tab -->
        <div class="search-popup-tabs">
          <div class="search-popup-tab" :class="{ active: searchPopupTab === 'hubs' }" @click="searchPopupTab = 'hubs'">
            {{ $t('channels_groups') }}
          </div>
          <div class="search-popup-tab" :class="{ active: searchPopupTab === 'chats' }" @click="searchPopupTab = 'chats'">
            {{ $t('channels_feed') }}
          </div>
          <div class="search-popup-tab" :class="{ active: searchPopupTab === 'my' }" @click="searchPopupTab = 'my'">
            {{ $t('channels_myChannels') }}
          </div>
        </div>

        <!-- 搜索结果列表 -->
        <div style="flex: 1; overflow-y: auto; padding: 0 16px;">
          <div v-if="searchPopupLoading" style="display: flex; justify-content: center; padding: 40px;">
            <van-loading size="24" />
          </div>

          <div v-else-if="searchPopupResults.length === 0 && searchPopupQuery.trim() && searchPopupHasSearched"
            style="text-align: center; padding: 40px; color: #8A8A8A;">
            <van-icon name="search" size="48" color="#ddd" />
            <div style="margin-top: 16px;">{{ $t('channels_noResults') }}</div>
            <van-button v-if="searchPopupTab === 'hubs'" type="primary" color="#E8543A" style="margin-top: 16px;" @click="goToCreateChannelFromSearch">
              {{ $t('channels_createNewGroup') }}: #{{ searchPopupQuery.trim() }}
            </van-button>
          </div>

          <div v-else-if="searchPopupResults.length === 0 && !searchPopupQuery.trim()" style="text-align: center; padding: 40px; color: #8A8A8A;">
            <van-icon name="search" size="48" color="#ddd" />
            <div style="margin-top: 16px;">{{ searchPopupTab === 'chats' ? $t('channels_searchHintChats') : searchPopupTab === 'hubs' ? $t('channels_searchHintGroups') : $t('channels_searchHintMy') }}</div>
          </div>

          <!-- Chats 搜索结果（session 卡片） -->
          <div v-if="searchPopupTab === 'chats'">
            <div v-for="session in searchPopupResults" :key="session.chatSessionId" class="channel-card"
              @click="openSessionFromSearch(session)">
              <div class="channel-card-info">
                <div v-if="session.channelName" class="channel-card-name">{{ session.channelName }}</div>
                <div v-if="session.userMsg || session.aiSummary" class="channel-last-session">
                  <van-icon name="chat-o" size="12" color="#8A8A8A" />
                  <span class="channel-last-session-summary">{{ $common.formatUserMsgPreview(session.userMsg) || session.aiSummary }}</span>
                </div>
                <div v-if="session.fromUserName" class="channel-card-meta" style="margin-top: 6px;">
                  <img v-if="session.fromUserAvatar" :src="$common.getFullImageUrl(session.fromUserAvatar, $config, 's')" style="width: 16px; height: 16px; border-radius: 50%; object-fit: cover;" />
                  <div v-else style="width: 16px; height: 16px; border-radius: 50%; background: #E8543A; display: flex; align-items: center; justify-content: center; color: white; font-size: 8px; font-weight: bold;">{{ (session.fromUserName || '?')[0] }}</div>
                  <span>{{ session.fromUserName }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Hubs / My 搜索结果（channel 卡片） -->
          <div v-else>
            <div v-for="channel in searchPopupResults" :key="channel.channelId" class="channel-card"
              @click="goToChannelDetailFromSearch(channel)">
              <div class="channel-card-avatar">
                <img v-if="channel.avatar" :src="$common.getFullImageUrl(channel.avatar, $config)" class="channel-avatar-img" />
                <div v-else class="channel-avatar-placeholder">
                  {{ (channel.name || '#')[0] }}
                </div>
              </div>
              <div class="channel-card-info">
                <div class="channel-card-name">{{ channel.name }}</div>
                <div v-if="channel.description" class="channel-card-desc">{{ channel.description }}</div>
                <div class="channel-card-meta">
                  <span>{{ channel.memberCnt || 0 }} {{ $t('channel_detail_members') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部创建 hub 按钮（仅 Hubs tab） -->
        <div v-if="searchPopupTab === 'hubs' && (searchPopupHasSearched || searchPopupResults.length > 0)" class="search-popup-create-btn" @click="goToCreateChannelFromSearchPopup">
          <van-icon name="add-o" size="18" color="#999" />
          <span>{{ $t('channels_createNewGroup') }}</span>
        </div>
      </div>
    </van-popup>

    <!-- Session Detail 弹框 -->
    <van-popup v-model="showSessionDetail" round position="bottom" :style="{ height: '90%' }">
      <div class="session-detail-popup">
        <div class="session-detail-header">
          <div class="session-detail-avatar">
            <img v-if="currentSession.fromUserAvatar" :src="$common.getFullImageUrl(currentSession.fromUserAvatar, $config, 's')" class="session-detail-avatar-img" />
            <div v-else class="session-detail-avatar-placeholder">{{ (currentSession.fromUserName || '?')[0] }}</div>
          </div>
          <div class="session-detail-user-info">
            <div class="session-detail-user-name">{{ currentSession.fromUserName || $t('anonymous') }}</div>
          </div>
          <van-icon v-if="currentSession.chatSessionId && currentSession.channelId" name="share-o" size="20" color="#666" style="cursor: pointer; margin-right: 12px;" @click.stop="handleShareSession(currentSession)" />
          <van-icon name="cross" size="20" color="#666" @click="showSessionDetail = false" style="cursor: pointer;" />
        </div>
        <div v-if="currentSession.userMsg || currentSession.aiSummary" class="session-detail-summary-card">
          <div class="session-detail-summary-inner">
            <div v-if="currentSession.userMsg" class="session-detail-usermsg-row">
              <span class="session-detail-usermsg-text">{{ $common.formatUserMsgPreview(currentSession.userMsg) }}</span>
            </div>
            <div v-if="currentSession.userMsg && currentSession.aiSummary" class="session-detail-summary-divider"></div>
            <div v-if="currentSession.aiSummary" class="session-detail-summary-row">
              <span class="session-detail-ai-tag">{{ $t('aiChat_matchSummaryLabel') }}</span>
              <span class="session-detail-ai-text">{{ currentSession.aiSummary }}</span>
            </div>
          </div>
        </div>
        <div v-if="currentSession.chatSessionId" class="session-detail-chat">
          <ChatBox v-if="showSessionDetail" :initialChatSessionId="String(currentSession.chatSessionId)"
            :channelId="currentSession.channelId" :channelName="currentSession.channelName" :isEmbedded="true"
            :currentAvatarUrl="$store.state.user && $store.state.user.avatar" :currentUserName="$store.state.user && ($store.state.user.userName || $store.state.user.email)"
            :showRequirementButtons="false" :showInputArea="false" :showUserMsgBanner="false"
            :chatMsgListStyle="{ minHeight: '0', maxHeight: 'none', backgroundColor: '#ECEAE6', padding: '12px' }" />
        </div>
      </div>
    </van-popup>

    <!-- Match History 弹框 -->
    <van-popup v-model="showMatchHistory" round position="bottom" :style="{ height: '80%' }">
      <div class="match-history-popup">
        <div class="match-history-header">
          <van-icon name="arrow-left" size="20" color="#4A4A4A" @click="showMatchHistory = false" />
          <span>{{ $t('chatSessionMatch_matchHistory') }}</span>
          <span></span>
        </div>
        <div v-if="matchHistoryLoading" style="display: flex; justify-content: center; padding: 40px;">
          <van-loading size="24" />
        </div>
        <div v-else-if="matchHistoryList.length === 0" style="text-align: center; padding: 40px; color: #8A8A8A;">
          <van-icon name="friends-o" size="48" color="#ddd" />
          <div style="margin-top: 16px;">{{ $t('chatSessionMatch_noMatches') }}</div>
        </div>
        <div v-else class="match-history-list" @scroll="onMatchHistoryScroll">
          <div v-for="(item, index) in matchHistoryList" :key="index"
            :class="['match-history-item', item.matchLevel === 'matched' ? 'match-history-item-matched' : 'match-history-item-interested']"
            @click="goToMatchDetail(item)">
            <van-icon name="arrow" class="match-history-arrow" />
            <div class="match-history-badge" :class="item.matchLevel === 'matched' ? 'badge-matched' : 'badge-interested'">
              {{ item.matchLevel === 'matched' ? $t('label_perfect_match') : $t('label_worth_a_look') }}
            </div>
            <div v-if="item.matchedUserMsg" class="match-history-user-msg">{{ $common.formatUserMsgPreview(item.matchedUserMsg) }}</div>
            <div v-if="item.matchedUserMsg && item.matchedAiSummary" style="height: 8px; border-bottom: 0.5px solid #E0DDD8; margin-bottom: 4px;"></div>
            <div v-if="item.matchedAiSummary" class="match-history-ai-summary">
              <span class="match-history-ai-icon">✨</span>
              <div style="flex: 1;">
                <span style="font-size: 11px; color: #8A8A8A;">{{ $t('aiChat_matchSummaryLabel') }}: </span>
                <span class="match-history-ai-text">{{ item.matchedAiSummary }}</span>
              </div>
            </div>
            <!-- 对方会话私有时展示提示（后端不下发内容） -->
            <div v-if="isPeerPrivate(item)" style="display: flex; align-items: flex-start; gap: 4px; margin-bottom: 4px;">
              <van-icon name="lock" size="12" color="#8A8A8A" style="margin-top: 3px; flex-shrink: 0;" />
              <span style="font-size: 12px; color: #8A8A8A; line-height: 1.4;">{{ $t('match_private_hint') }}</span>
            </div>
            <div class="match-history-meta">
              <img v-if="item.matchedAvatar" :src="$common.getFullImageUrl(item.matchedAvatar, $config, 's')" class="match-history-avatar" />
              <div v-else class="match-history-avatar-placeholder">{{ (item.matchedUserName || 'A')[0] }}</div>
              <span>{{ item.matchedUserName || $t('anonymous') }}</span>
              <span v-if="item.createdAt" style="margin-left: auto; color: #bbb; font-size: 11px;">{{ $common.formatDateTime(item.createdAt) }}</span>
            </div>
            <div v-if="item.briefMatchReason" class="match-history-reason">
              <span>⇄</span>
              <span>{{ item.briefMatchReason }}</span>
            </div>
          </div>
          <div v-if="matchHistoryHasMore" style="padding: 12px; text-align: center;">
            <van-button size="small" :loading="matchHistoryLoadingMore" @click="loadMoreMatchHistory">{{ $t('loadMore') || 'Load more' }}</van-button>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import { Icon, Toast, Button, PullRefresh, Empty, Loading, Popup, ActionSheet, List } from 'vant'
import ChatBox from './ChatBox.vue'
import { shareSession } from '@/services/share.js'
import { AI_DIRECT_CHAT_ENABLED } from '@/assets/feature_flags.js'

Vue.use(Icon)
Vue.use(Toast)
Vue.use(Button)
Vue.use(PullRefresh)
Vue.use(Empty)
Vue.use(Loading)
Vue.use(Popup)
Vue.use(ActionSheet)
Vue.use(List)

let mySearchDebounceTimer = null
let searchPopupDebounceTimer = null

export default {
  name: 'Channels',
  components: {
    'ChatBox': ChatBox,
  },
  data() {
    return {
      activeTab: 'my', // 'my' | 'discover'  — My Hubs 默认首 tab，为空时自动切到 Discover
      showDraftBanner: false, // 有未发布的 AI 会话草稿时展示续写 banner
      draftBannerDismissed: false,
      // My Hubs
      myChannels: [],
      myLoading: false,
      myRefreshing: false,
      myHasLoaded: false,
      myHasMore: true,
      myCurPage: 1,
      myPageSize: 50,
      mySearchQuery: '',
      mySearchResults: [],
      mySearchLoading: false,
      myAutoSwitchChecked: false, // 首次加载 My Hubs 为空时是否已自动切到 Discover
      // Discover
      discoverChannels: [],
      discoverLoading: false,
      discoverRefreshing: false,
      discoverHasLoaded: false,
      discoverHasMore: true,
      discoverCurPage: 1,
      discoverPageSize: 20,
      // Join by code
      showJoinByCode: false,
      joinCode: '',
      joinCodeError: '',
      joiningByCode: false,
      // Join by QR
      showJoinByQR: false,
      // Search popup
      showSearchPopup: false,
      searchPopupTab: 'hubs',
      mySearched: false,
      hubsSearched: false,
      chatsSearched: false,
      // Feed (discover sub-tab)
      discoverSubTab: 'hubs', // 'hubs' | 'feed'（默认 Hubs）
      feedList: [],
      feedSort: 'recommend',
      feedLoading: false,
      feedRefreshing: false,
      feedCurPage: 1,
      feedPageSize: 10,
      feedHasMore: true,
      feedHasLoaded: false,
      expandedFeedComments: {},
      // Session detail popup
      showSessionDetail: false,
      currentSession: {},
      // Match history popup
      showMatchHistory: false,
      matchHistoryLoading: false,
      matchHistoryList: [],
      matchHistoryChatSessionId: '',
      matchHistoryCurPage: 1,
      matchHistoryHasMore: false,
      matchHistoryLoadingMore: false,
      // Action sheet
      showActionSheet: false,
      searchPopupQuery: '',
      searchPopupResults: [],
      searchPopupLoading: false,
      searchPopupHasSearched: false,
    }
  },
  computed: {
    displayChannels() {
      return this.myChannels
    },
    myUserId() {
      return String((this.$store.state.user && this.$store.state.user.userId) || '')
    },
    actionSheetActions() {
      return [
        { name: this.$t('channels_joinByCode'), callback: () => { this.showActionSheet = false; this.showJoinByCode = true } },
      ]
    }
  },
  watch: {
    activeTab(newVal) {
      if (newVal === 'my') {
        if (this.myChannels.length === 0) {
          this.refreshMyChannels()
        }
      } else {
        // Discover tab: 默认 Hubs 子视图，加载 hub 列表
        this.discoverSubTab = 'hubs'
        if (this.discoverChannels.length === 0 && !this.discoverHasLoaded) {
          this.refreshDiscoverChannels()
        }
      }
    },
    '$store.state.alreadyLogin' (newVal) {
      // 登录/登出后重置 My Hubs 状态并重新加载；空列表时自动切到 Discover
      this.myAutoSwitchChecked = false
      if (newVal) {
        this.refreshMyChannels()
      } else {
        this.myChannels = []
        this.myHasLoaded = false
      }
    },
    searchPopupTab() {
      // 切换 tab 时，如果该 tab 还没搜过且有查询词，则触发搜索
      var q = this.searchPopupQuery.trim()
      if (!q) {
        this.searchPopupResults = []
        return
      }
      if (this.searchPopupTab === 'chats' && !this.chatsSearched) {
        this.searchPopupLoading = true
        this.performSearchSessions(q)
      } else if (this.searchPopupTab === 'my' && !this.mySearched) {
        this.searchPopupLoading = true
        this.performSearchPopup(q)
      } else if (this.searchPopupTab === 'hubs' && !this.hubsSearched) {
        this.searchPopupLoading = true
        this.performSearchPopup(q)
      } else {
        // 已搜过，切换回展示已缓存的结果
        this.searchPopupLoading = true
        this.performSearchPopup(q)
      }
    }
  },
  mounted() {
    // 默认 My Hubs tab，加载我的 hub 列表；为空时由 loadMyChannels 自动切到 Discover
    this.refreshMyChannels()
    // 检查是否有未发布的独立 AI 会话草稿（有则展示续写 banner）
    this.checkDraftSession()
    // 列表已改为随窗口滚动，滚动加载监听从列表元素挂到 window 上
    window.addEventListener('scroll', this.onScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    // 检查是否有未发布的独立 AI 会话草稿（status=0），有则展示续写 banner；
    // 点击 banner 跳 /ai，ChatBox 的 loadLastSession 会自动恢复该草稿继续聊。
    // AI 直聊入口开关关闭时直接不检查（banner 永远不展示）
    async checkDraftSession() {
      if (!AI_DIRECT_CHAT_ENABLED) return
      if (this.draftBannerDismissed) return
      if (!this.$store.state.alreadyLogin) return
      try {
        const response = await axios.post('/api/chat/get_last_chat_session', {})
        if (response.data.code === 0 && response.data.data && response.data.data.chatSession) {
          const lastSession = response.data.data.chatSession
          if (String(lastSession.status || '0') !== '1') {
            this.showDraftBanner = true
          }
        }
      } catch (e) {
        // 检查失败静默忽略，不影响正常列表
      }
    },
    goDraft() {
      this.showDraftBanner = false
      // 打开全局 AI 对话弹窗（ChatBox 挂载时 loadLastSession 自动恢复草稿），不再跳 /ai 页
      this.$store.commit('setShowAiChatPopup', true)
    },
    dismissDraftBanner() {
      this.showDraftBanner = false
      this.draftBannerDismissed = true
    },
    // 对方会话是否私有（后端对私有会话不下发 matchedUserMsg/matchedAiSummary）
    isPeerPrivate (item) {
      return item.matchedPublic !== undefined && item.matchedPublic !== null && String(item.matchedPublic) !== '1'
    },
    // ===== My Hubs =====
    async refreshMyChannels() {
      this.myCurPage = 1
      this.myChannels = []
      this.myHasMore = true
      this.mySearchQuery = ''
      this.mySearchResults = []
      await this.loadMyChannels()
    },
    async onMyRefresh() {
      this.myRefreshing = true
      await this.refreshMyChannels()
      this.myRefreshing = false
    },
    async loadMyChannels(append = false) {
      if (this.myLoading) return
      if (!append && !this.myHasMore && this.myChannels.length > 0) return
      if (!this.$store.state.alreadyLogin) {
        this.myHasLoaded = true
        return
      }
      this.myLoading = true
      try {
        const response = await axios.post('/api/channel/my_channels', {
          pageSize: this.myPageSize,
          curPage: this.myCurPage
        })
        const body = response.data
        if (body.code === 0 && body.data) {
          const channels = (body.data.channels || []).map(c => this.normalizeChannel(c))
          // 去重
          const existingIds = new Set(this.myChannels.map(ch => ch.channelId))
          const newChannels = channels.filter(c => !existingIds.has(c.channelId))
          if (append) {
            this.myChannels = this.myChannels.concat(newChannels)
          } else {
            this.myChannels = channels
          }
          this.myHasMore = channels.length >= this.myPageSize
        } else {
          if (!append) {
            this.myChannels = []
          }
          this.myHasMore = false
        }
      } catch (error) {
        console.error('[Channels] loadMyChannels error:', error)
        Toast.fail(this.$t('dataLoadingFailed'))
      } finally {
        this.myLoading = false
        this.myHasLoaded = true
        // 首次加载（如登录后的新用户）My Hubs 为空时，自动切到 Discover tab
        if (!append && !this.myAutoSwitchChecked) {
          this.myAutoSwitchChecked = true
          if (this.activeTab === 'my' && this.$store.state.alreadyLogin && this.myChannels.length === 0) {
            this.activeTab = 'discover'
          }
        }
      }
    },
    loadMoreMyChannels() {
      if (this.myHasMore && !this.myLoading) {
        this.myCurPage++
        this.loadMyChannels(true)
      }
    },
    // My Hubs 搜索
    onMySearchInput() {
      clearTimeout(mySearchDebounceTimer)
      const query = this.mySearchQuery.trim()
      if (!query) {
        this.mySearchResults = []
        return
      }
      this.mySearchLoading = true
      mySearchDebounceTimer = setTimeout(() => {
        this.performMySearch(query)
      }, 500)
    },
    async performMySearch(query) {
      if (!this.$store.state.alreadyLogin) return
      try {
        const userId = this.$common.getUserId()
        const response = await axios.post('/api/channel/search', {
          query: query,
          filterUserId: userId,
          pageSize: 20,
          curPage: 1
        })
        const body = response.data
        if (body.code === 0 && body.data) {
          this.mySearchResults = (body.data.channels || []).map(c => this.normalizeChannel(c))
        } else {
          this.mySearchResults = []
        }
      } catch (error) {
        console.error('[Channels] search my channels error:', error)
        this.mySearchResults = []
      } finally {
        this.mySearchLoading = false
      }
    },
    clearMySearch() {
      this.mySearchQuery = ''
      this.mySearchResults = []
    },
    // ===== Discover =====
    async refreshDiscoverChannels() {
      this.discoverCurPage = 1
      this.discoverChannels = []
      this.discoverHasMore = true
      await this.loadDiscoverChannels()
    },
    async onDiscoverRefresh() {
      this.discoverRefreshing = true
      await this.refreshDiscoverChannels()
      this.discoverRefreshing = false
    },
    async loadDiscoverChannels(append = false) {
      if (this.discoverLoading) return
      if (!append && !this.discoverHasMore && this.discoverChannels.length > 0) return
      this.discoverLoading = true
      try {
        const response = await axios.post('/api/channel/search', {
          query: '',
          pageSize: this.discoverPageSize,
          curPage: this.discoverCurPage
        })
        const body = response.data
        if (body.code === 0 && body.data) {
          const channels = (body.data.channels || []).map(c => this.normalizeChannel(c))
          const existingIds = new Set(this.discoverChannels.map(ch => ch.channelId))
          const newChannels = channels.filter(c => !existingIds.has(c.channelId))
          if (append) {
            this.discoverChannels = this.discoverChannels.concat(newChannels)
          } else {
            this.discoverChannels = channels
          }
          this.discoverHasMore = channels.length >= this.discoverPageSize
        } else {
          if (!append) {
            this.discoverChannels = []
          }
          this.discoverHasMore = false
        }
      } catch (error) {
        console.error('[Channels] loadDiscoverChannels error:', error)
      } finally {
        this.discoverLoading = false
        this.discoverHasLoaded = true
      }
    },
    loadMoreDiscoverChannels() {
      if (this.discoverHasMore && !this.discoverLoading) {
        this.discoverCurPage++
        this.loadDiscoverChannels(true)
      }
    },
    // ===== Feed =====
    async refreshFeed() {
      this.feedCurPage = 1
      this.feedList = []
      this.feedHasMore = true
      this.feedLoading = false
      await this.loadFeed()
    },
    async onFeedRefresh() {
      this.feedRefreshing = true
      await this.refreshFeed()
      this.feedRefreshing = false
    },
    async loadFeed(append) {
      if (this.feedLoading) return
      if (append && !this.feedHasMore) return
      this.feedLoading = true
      try {
        const response = await axios.post('/api/channel/feed', {
          sort: this.feedSort,
          pageSize: this.feedPageSize,
          curPage: append ? this.feedCurPage : 1
        })
        const body = response.data
        if (body.code === 0 && body.data) {
          const sessions = body.data.sessions || []
          if (append) {
            const seen = new Set(this.feedList.map(s => s.chatSessionId))
            const newSessions = sessions.filter(s => !seen.has(s.chatSessionId))
            this.feedList = this.feedList.concat(newSessions)
          } else {
            this.feedList = sessions
          }
          this.feedHasMore = sessions.length >= this.feedPageSize
        } else {
          if (!append) this.feedList = []
          this.feedHasMore = false
        }
      } catch (e) {
        console.error('[Channels] loadFeed error:', e)
        if (!append) this.feedList = []
      } finally {
        this.feedLoading = false
        this.feedHasLoaded = true
      }
    },
    loadMoreFeed() {
      if (this.feedHasMore && !this.feedLoading) {
        this.feedCurPage++
        this.loadFeed(true)
      }
    },
    switchDiscoverSubTab(tab) {
      if (this.discoverSubTab === tab) return
      this.discoverSubTab = tab
      if (tab === 'feed') {
        this.refreshFeed()
      } else {
        if (this.discoverChannels.length === 0) {
          this.refreshDiscoverChannels()
        }
      }
    },
    // ===== Scroll handling =====
    onScroll() {
      // 页面已改为随窗口滚动，用窗口滚动距离判断是否滚动到底部
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0
      const scrollHeight = document.documentElement.scrollHeight
      const clientHeight = window.innerHeight || document.documentElement.clientHeight
      // 滚动到底部附近时加载更多
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        if (this.activeTab === 'my') {
          this.loadMoreMyChannels()
        } else if (this.discoverSubTab === 'feed') {
          this.loadMoreFeed()
        } else {
          this.loadMoreDiscoverChannels()
        }
      }
    },
    // ===== Search =====
    openSearch(preferredTab) {
      if (preferredTab) {
        this.searchPopupTab = preferredTab
      } else {
        this.searchPopupTab = this.activeTab === 'my' ? 'my' : 'hubs'
      }
      this.searchPopupQuery = ''
      this.searchPopupResults = []
      this.searchPopupHasSearched = false
      this.mySearched = false
      this.hubsSearched = false
      this.chatsSearched = false
      this.showSearchPopup = true
    },
    // ===== Action sheet =====
    goToCreateChannel() {
      this.showActionSheet = false
      this.$router.push({ name: 'ChannelCreate' })
    },
    // ===== Join by QR =====
    openQRScanner() {
      // Web 端无法直接扫码，引导用户用手机扫码或手动输入
      this.showJoinByQR = false
      this.showJoinByCode = true
    },
    // ===== Join by code =====
    async joinByCode() {
      const code = this.joinCode.trim().toUpperCase()
      if (!code) return
      this.joiningByCode = true
      this.joinCodeError = ''
      try {
        const response = await axios.post('/api/channel/get_by_code', { channelCode: code })
        const body = response.data
        if (body.code === 0 && body.data && body.data.channel) {
          const channel = this.normalizeChannel(body.data.channel)
          const joined = body.data.joined
          if (!joined) {
            await axios.post('/api/channel/join', { channelId: channel.channelId })
          }
          this.showJoinByCode = false
          this.joinCode = ''
          this.goToChannelDetail(channel)
        } else {
          this.joinCodeError = this.$t('channels_joinByCodeNotFound')
        }
      } catch (error) {
        console.error('[Channels] joinByCode error:', error)
        this.joinCodeError = this.$t('channels_joinByCodeNotFound')
      } finally {
        this.joiningByCode = false
      }
    },
    // ===== Search popup =====
    onSearchPopupInput() {
      clearTimeout(searchPopupDebounceTimer)
      const query = this.searchPopupQuery.trim()
      if (!query) {
        this.searchPopupResults = []
        this.searchPopupHasSearched = false
        this.mySearched = false
        this.hubsSearched = false
        this.chatsSearched = false
        return
      }
      this.searchPopupLoading = true
      this.searchPopupHasSearched = true
      searchPopupDebounceTimer = setTimeout(() => {
        this.performSearchPopup(query)
      }, 300)
    },
    clearSearchPopup() {
      clearTimeout(searchPopupDebounceTimer)
      this.searchPopupQuery = ''
      this.searchPopupResults = []
      this.searchPopupHasSearched = false
      this.mySearched = false
      this.hubsSearched = false
      this.chatsSearched = false
    },
    async performSearchPopup(query) {
      // Chats tab: dispatch to separate search_sessions call
      if (this.searchPopupTab === 'chats') {
        this.chatsSearched = true
        return this.performSearchSessions(query)
      }
      try {
        var params = { query: query, pageSize: 20, curPage: 1 }
        if (this.searchPopupTab === 'my') {
          params.filterUserId = this.$store.state.user && this.$store.state.user.userId || ''
          this.mySearched = true
        } else {
          this.hubsSearched = true
        }
        const response = await axios.post('/api/channel/search', params)
        const body = response.data
        if (body.code === 0 && body.data) {
          this.searchPopupResults = (body.data.channels || []).map(c => this.normalizeChannel(c))
        } else {
          this.searchPopupResults = []
        }
      } catch (error) {
        console.error('[Channels] search popup error:', error)
        this.searchPopupResults = []
      } finally {
        this.searchPopupLoading = false
      }
    },
    async performSearchSessions(query) {
      try {
        const response = await axios.post('/api/channel/search_sessions', {
          query: query,
          pageSize: 20,
          curPage: 1
        })
        const body = response.data
        if (body.code === 0 && body.data) {
          this.searchPopupResults = body.data.sessions || []
        } else {
          this.searchPopupResults = []
        }
      } catch (error) {
        console.error('[Channels] search sessions error:', error)
        this.searchPopupResults = []
      } finally {
        this.searchPopupLoading = false
      }
    },
    openSessionFromSearch(session) {
      this.showSearchPopup = false
      this.openSessionDetail(session)
    },
    goToCreateChannelFromSearch() {
      const name = this.searchPopupQuery.trim()
      this.showSearchPopup = false
      this.searchPopupQuery = ''
      this.searchPopupResults = []
      this.$router.push({ path: '/channel-create', query: { name } })
    },
    goToCreateChannelFromSearchPopup() {
      // 从搜索弹窗底部按钮跳转，不携带搜索词
      this.showSearchPopup = false
      this.searchPopupQuery = ''
      this.searchPopupResults = []
      this.$router.push({ path: '/channel-create' })
    },
    goToChannelDetailFromSearch(channel) {
      this.showSearchPopup = false
      this.searchPopupQuery = ''
      this.searchPopupResults = []
      this.goToChannelDetail(channel)
    },
    // ===== Feed card helpers =====
    parseFirstMediaContent(str) {
      if (!str) return null
      try {
        var obj = JSON.parse(str)
        if (!obj || !obj.type) return null
        return obj
      } catch (e) {
        return null
      }
    },
    isUserMsgMixed(userMsg) {
      if (!userMsg) return false
      try {
        var parsed = Array.isArray(userMsg) ? userMsg : JSON.parse(userMsg)
        return Array.isArray(parsed) && parsed.length > 0 && parsed[0] && parsed[0].type
      } catch (e) {
        return false
      }
    },
    parseMixedContent(userMsg) {
      if (!userMsg) return []
      try {
        var parsed = Array.isArray(userMsg) ? userMsg : JSON.parse(userMsg)
        if (!Array.isArray(parsed)) return []
        return parsed
      } catch (e) {
        return []
      }
    },
    parseCommentPreviews(jsonStr) {
      if (!jsonStr) return []
      try {
        var arr = JSON.parse(jsonStr)
        return Array.isArray(arr) ? arr : []
      } catch (e) {
        return []
      }
    },
    getStaticMapUrl(lat, lng) {
      if (!lat || !lng) return ''
      var z = 14
      var n = Math.pow(2, z)
      var x = Math.floor((parseFloat(lng) + 180) / 360 * n)
      var latRad = parseFloat(lat) * Math.PI / 180
      var y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n)
      return 'https://b.basemaps.cartocdn.com/light_all/' + z + '/' + x + '/' + y + '.png'
    },
    toggleFeedCommentExpand(sessionId) {
      Vue.set(this.expandedFeedComments, sessionId, !this.expandedFeedComments[sessionId])
    },
    isFeedCommentExpanded(sessionId) {
      return !!this.expandedFeedComments[sessionId]
    },
    // ===== Common =====
    normalizeChannel(c) {
      return {
        channelId: String(c.channelId || ''),
        name: c.name || '',
        description: c.description || '',
        avatar: c.avatar || '',
        memberCnt: c.memberCnt || 0,
        chatSessionCnt: c.chatSessionCnt || 0,
        channelCode: c.channelCode || '',
        lastSession: c.lastSession || null
      }
    },
    // lastSession 是否对当前查看者隐藏内容：由后端统一判断（contentHidden），前端不要自己复算
    isPrivateSessionForViewer (session) {
      return !!(session && session.contentHidden)
    },
    goToChannelDetail(channel) {
      this.$router.push({ path: '/channel/' + channel.channelId, query: { channelName: channel.name } })
    },
    openChannelInNewTab(session) {
      const url = `/app/#/channel/${session.channelId}?channelName=${encodeURIComponent(session.channelName || '')}&chatSessionId=${session.chatSessionId || ''}`
      window.open(url, '_blank')
    },
    openSessionDetail(session) {
      this.currentSession = session
      this.showSessionDetail = true
    },
    async handleShareSession(session) {
      await shareSession(session, this.$i18n)
    },
    // ===== Match History =====
    async viewMatchHistory(chatSessionId) {
      this.matchHistoryChatSessionId = chatSessionId
      this.showMatchHistory = true
      this.matchHistoryLoading = true
      this.matchHistoryCurPage = 1
      this.matchHistoryHasMore = false
      try {
        const response = await axios.post('/api/chat/get_match_history', {
          chatSessionId: chatSessionId,
          pageSize: 10,
          curPage: 1
        })
        if (response.data.code === 0 && response.data.data) {
          const details = response.data.data.matchDetails || []
          this.matchHistoryList = details
          this.matchHistoryHasMore = details.length >= 10
        } else {
          this.matchHistoryList = []
        }
      } catch (e) {
        console.error('[Channels] viewMatchHistory error:', e)
        this.matchHistoryList = []
      } finally {
        this.matchHistoryLoading = false
      }
    },
    async loadMoreMatchHistory() {
      if (this.matchHistoryLoadingMore || !this.matchHistoryHasMore) return
      this.matchHistoryLoadingMore = true
      this.matchHistoryCurPage++
      try {
        const response = await axios.post('/api/chat/get_match_history', {
          chatSessionId: this.matchHistoryChatSessionId,
          pageSize: 10,
          curPage: this.matchHistoryCurPage
        })
        if (response.data.code === 0 && response.data.data) {
          const details = response.data.data.matchDetails || []
          this.matchHistoryList = this.matchHistoryList.concat(details)
          this.matchHistoryHasMore = details.length >= 10
        } else {
          this.matchHistoryHasMore = false
        }
      } catch (e) {
        this.matchHistoryHasMore = false
      } finally {
        this.matchHistoryLoadingMore = false
      }
    },
    onMatchHistoryScroll(e) {
      const el = e.target
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
        this.loadMoreMatchHistory()
      }
    },
    goToMatchDetail(item) {
      const url = `/app/#/matched-session?currentChatSessionId=${this.matchHistoryChatSessionId}&matchedChatSessionId=${item.matchedChatSessionId}`
      if (item.matchDetailId) {
        window.open(url + `&matchDetailId=${item.matchDetailId}`, '_blank')
      } else {
        window.open(url, '_blank')
      }
    },
  }
}
</script>

<style scoped>
.channels-page {
  background: #F7F5F2;
  height: 100%;
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
}

/* 草稿续写 banner */
.draft-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 16px 10px;
  padding: 10px 14px;
  background: #FFF8F6;
  border: 1px solid #FEF0ED;
  border-radius: 12px;
  cursor: pointer;
}

.draft-banner-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.draft-banner-texts {
  flex: 1;
  min-width: 0;
}

.draft-banner-title {
  font-size: 13px;
  font-weight: 600;
  color: #1A1A1A;
}

.draft-banner-subtitle {
  font-size: 11px;
  color: #8A8A8A;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.channels-top-bar {
  display: flex;
  align-items: center;
  background: white;
  border-bottom: 1px solid #E0DDD8;
  flex-shrink: 0;
  padding: 8px 12px;
  position: relative;
}

.channels-tabs {
  display: flex;
  padding: 3px;
  background: #f5f5f5;
  border-radius: 24px;
}

.channels-tab {
  padding: 7px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #8A8A8A;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.channels-tab.active {
  background: #fff;
  color: #E8543A;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
}

.channels-top-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
}

.channels-icon-btn {
  width: 36px;
  height: 36px;
  background: #fff;
  border: 1px solid #E0DDD8;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #4A4A4A;
  transition: all 0.2s ease;
}

.channels-icon-btn:hover {
  border-color: #E8543A;
  color: #E8543A;
}

.channels-icon-btn.primary {
  background: #E8543A;
  border-color: #E8543A;
  color: #fff;
}

.channels-icon-btn.primary:hover {
  background: #C93E1A;
}

.channels-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: white;
  flex-shrink: 0;
}

.channels-search-inner {
  flex: 1;
  display: flex;
  align-items: center;
  background: #E0DDD8;
  border-radius: 20px;
  padding: 0 12px;
}

.join-by-code-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  height: 32px;
  background: #E0DDD8;
  border-radius: 20px;
  font-size: 13px;
  color: #8A8A8A;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.channels-list {
  flex: 1;
  padding: 8px 12px;
}

.channel-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  text-align: left;
}

.channel-card-avatar {
  flex-shrink: 0;
}

.channel-avatar-img {
  width: 48px;
  height: 48px;
  border-radius: 16%;
  object-fit: cover;
}

.channel-avatar-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 16%;
  background: #E0DDD8;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.54);
  font-size: 18px;
  font-weight: 500;
}

.channel-card-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.channel-card-name {
  font-size: 15px;
  font-weight: 600;
  color: #1A1A1A;
  margin-bottom: 4px;
}

.channel-card-desc {
  font-size: 12px;
  color: #8A8A8A;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.channel-card-meta {
  font-size: 11px;
  color: #8A8A8A;
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 3px;
}

/* Discover 卡片最新消息：浅灰 chip + 蓝色图标，与上方 description 纯文本区分开 */
.channel-card-lastmsg {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex: 1;
  min-width: 0;
  font-size: 11px;
  background: #F7F5F2;
  border-radius: 6px;
  padding: 2px 6px;
}

.channel-card-lastmsg-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.channel-last-session {
  margin-top: 6px;
  padding: 8px 10px;
  background: #E8F7FB;
  border-radius: 8px;
  border: 1px solid #E0DDD8;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.channel-last-session-summary {
  color: #4A4A4A;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.channel-last-session-user {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #4A4A4A;
  flex-shrink: 0;
}

.channel-last-session-avatar {
  width: 16px;
  height: 16px;
  border-radius: 16%;
  object-fit: cover;
}

.channel-last-session-avatar-placeholder {
  width: 16px;
  height: 16px;
  border-radius: 16%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 8px;
}



.join-by-code-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.join-by-code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid #E0DDD8;
}

.search-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-popup-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.search-popup-tabs {
  display: flex;
  border: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.search-popup-tab {
  flex: 1;
  text-align: center;
  padding: 10px 0;
  font-size: 14px;
  color: #8A8A8A;
  cursor: pointer;
  position: relative;
}

.search-popup-tab.active {
  color: #E8543A;
  font-weight: 600;
}

.search-popup-tab.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 2px;
  background: #E8543A;
  border-radius: 1px;
}

.search-popup-search-inner {
  flex: 1;
  display: flex;
  align-items: center;
  background: #E0DDD8;
  border-radius: 8px;
  padding: 0 12px;
}

.search-popup-create-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 16px;
  color: #8A8A8A;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid #E0DDD8;
  flex-shrink: 0;
  background: white;
}

.search-popup-create-btn:active {
  background: #F7F5F2;
}

/* ===== Discover Sub-Tab Bar ===== */
.discover-sub-tabs {
  display: flex;
  justify-content: center;
  margin: 8px 16px;
  padding: 3px;
  background: #f5f5f5;
  border-radius: 24px;
  min-width: 0;
  overflow: hidden;
}
.discover-sub-tab {
  flex: 1;
  min-width: 0;
  text-align: center;
  padding: 8px 0;
  font-size: 13px;
  font-weight: 500;
  color: #8A8A8A;
  border-radius: 20px;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}
.discover-sub-tab.active {
  background: #fff;
  color: #E8543A;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
}

/* ===== Feed Card ===== */
.feed-card {
  margin: 6px 16px;
  padding: 14px;
  background: #fff;
  border: 1px solid #E0DDD8;
  border-radius: 14px;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
  text-align: left;
}
.feed-card:active {
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.feed-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.feed-card-channel {
  font-size: 11px;
  font-weight: 600;
  color: #4A4A4A;
}
.feed-card-channel-hub {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.feed-card-channel-avatar-img {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
}
.feed-card-channel-avatar {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: #F7F5F2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: #8A8A8A;
  flex-shrink: 0;
}
.feed-card-time {
  font-size: 11px;
  color: #8A8A8A;
}
.feed-card-user-top {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.feed-card-avatar-top {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.feed-card-avatar-top-placeholder {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  color: white;
  flex-shrink: 0;
}
.feed-card-username-top {
  font-size: 11px;
  font-weight: 600;
  color: #4A4A4A;
}
.feed-card-msg {
  font-size: 14px;
  font-weight: 600;
  color: #1A1A1A;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.feed-card-summary {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 8px;
}
.feed-card-ai-tag {
  display: inline-block;
  padding: 0 5px;
  background: #FEF0ED;
  color: #8A8A8A;
  font-size: 10px;
  border-radius: 4px;
  font-weight: 500;
  flex-shrink: 0;
  line-height: 1.4;
}
.feed-card-ai-text {
  font-size: 11px;
  color: #8A8A8A;
  line-height: 1.4;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.feed-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.feed-card-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.feed-card-action {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  color: #8A8A8A;
  font-size: 12px;
  padding: 4px 6px;
  border-radius: 8px;
  transition: background 0.2s;
}
.feed-card-action:active {
  background: rgba(0, 0, 0, 0.05);
}
.feed-card-user {
  display: flex;
  align-items: center;
  gap: 6px;
}
.feed-card-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
}
.feed-card-avatar-placeholder {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: white;
}
.feed-card-username {
  font-size: 12px;
  color: #8A8A8A;
}
.feed-card-go {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  background: #E8543A;
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 700;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(232, 84, 58, 0.3);
  transition: all 0.2s;
}
.feed-card-go:active {
  opacity: 0.85;
  transform: scale(0.97);
}
.feed-card-match {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  background: rgba(61, 174, 207, 0.1);
  border: 0.5px solid rgba(61, 174, 207, 0.3);
  border-radius: 12px;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 6px;
}
.feed-card-match span {
  font-size: 11px;
  font-weight: 600;
  color: #2A8FAD;
}

/* 持续匹配中动效：icon 呼吸 + 涟漪扩散（仅 public 需求） */
.match-live-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  margin-right: 2px;
}
.match-live-icon .match-live-core {
  position: relative;
  z-index: 1;
  animation: matchLivePulse 1.6s ease-in-out infinite;
}
.match-live-ripple {
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  border: 1px solid rgba(61, 174, 207, 0.7);
  border-radius: 50%;
  opacity: 0;
  animation: matchLiveRipple 1.6s ease-out infinite;
  pointer-events: none;
}
.match-live-ripple2 {
  animation-delay: 0.8s;
}
@keyframes matchLiveRipple {
  0% { transform: scale(0.6); opacity: 0.8; }
  100% { transform: scale(1.9); opacity: 0; }
}
@keyframes matchLivePulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
.feed-card-media {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.feed-card-media-img {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  object-fit: cover;
}
.feed-card-media-text {
  font-size: 11px;
  color: #4A4A4A;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}
.feed-card-media-location {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid rgba(224, 221, 216, 0.4);
  overflow: hidden;
  max-width: 140px;
}
.feed-card-media-map {
  width: 140px;
  height: 60px;
  object-fit: cover;
}
.feed-card-media-location-info {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 6px;
  font-size: 10px;
  color: #4A4A4A;
  overflow: hidden;
}
.feed-card-media-location-info span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.feed-card-comments {
  margin-top: 8px;
  padding: 8px;
  background: rgba(255,255,255,0.6);
  border-radius: 6px;
  border: 1px solid rgba(224, 221, 216, 0.4);
}
.feed-card-comment-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 8px;
}
.feed-card-comment-item:last-child {
  margin-bottom: 0;
}
.feed-card-comment-avatar {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  margin-top: 1px;
}
.feed-card-comment-avatar-default {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 9px;
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 1px;
}
.feed-card-comment-body {
  flex: 1;
  min-width: 0;
  text-align: left;
}
.feed-card-comment-name {
  font-size: 11px;
  font-weight: 600;
  color: #4A4A4A;
  margin-bottom: 1px;
}
.feed-card-comment-content {
  font-size: 12px;
  color: #1A1A1A;
  line-height: 1.4;
  word-break: break-word;
}
.feed-card-comment-reply-to {
  color: #8A8A8A;
  font-size: 12px;
}
.feed-card-comment-more {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-size: 11px;
  color: #8A8A8A;
  font-weight: 400;
  cursor: pointer;
}

/* ===== Session Detail Popup ===== */
.session-detail-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.session-detail-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #E0DDD8;
  flex-shrink: 0;
}
.session-detail-avatar {
  flex-shrink: 0;
}
.session-detail-avatar-img {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}
.session-detail-avatar-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: white;
}
.session-detail-user-info {
  flex: 1;
  min-width: 0;
}
.session-detail-user-name {
  font-size: 15px;
  font-weight: 600;
  color: #1A1A1A;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.session-detail-summary-card {
  margin: 12px 16px 0;
  padding: 12px;
  background: #F7F5F2;
  border-radius: 12px;
}
.session-detail-summary-inner {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.session-detail-usermsg-text {
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1.5;
}
.session-detail-summary-divider {
  height: 1px;
  background: #E0DDD8;
}
.session-detail-summary-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}
.session-detail-ai-tag {
  display: inline-block;
  padding: 0 5px;
  background: #FEF0ED;
  color: #8A8A8A;
  font-size: 10px;
  border-radius: 4px;
  font-weight: 500;
  flex-shrink: 0;
  line-height: 1.4;
}
.session-detail-ai-text {
  font-size: 11px;
  color: #8A8A8A;
  line-height: 1.4;
  flex: 1;
}
.session-detail-chat {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* ===== Match History Popup ===== */
.match-history-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.match-history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #E0DDD8;
  font-size: 16px;
  font-weight: 600;
  color: #1A1A1A;
  flex-shrink: 0;
}
.match-history-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
}
.match-history-item {
  position: relative;
  padding: 12px;
  margin-bottom: 8px;
  background: #fff;
  border: 1px solid #E0DDD8;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
}
.match-history-item-matched {
  border-left: 3px solid #E8543A;
}
.match-history-item-interested {
  border-left: 3px solid #3DAECF;
}
.match-history-arrow {
  position: absolute;
  top: 12px;
  right: 12px;
  color: #ccc;
}
.match-history-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  margin-bottom: 8px;
}
.badge-matched {
  background: #FEF0ED;
  color: #E8543A;
}
.badge-interested {
  background: #E8F7FB;
  color: #3DAECF;
}
.match-history-user-msg {
  font-size: 14px;
  font-weight: 600;
  color: #1A1A1A;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.match-history-ai-summary {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
}
.match-history-ai-icon {
  flex-shrink: 0;
}
.match-history-ai-text {
  font-size: 12px;
  color: #8A8A8A;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.match-history-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #8A8A8A;
  margin-bottom: 4px;
}
.match-history-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
}
.match-history-avatar-placeholder {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #F7F5F2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #8A8A8A;
}
.match-history-reason {
  display: flex;
  gap: 4px;
  font-size: 11px;
  color: #3DAECF;
  margin-top: 4px;
}

/* ===== My Hubs Empty State ===== */
.my-hubs-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
}
.my-hubs-empty-title {
  margin-top: 16px;
  font-size: 16px;
  font-weight: 600;
  color: #1A1A1A;
}
.my-hubs-empty-subtitle {
  margin-top: 8px;
  font-size: 14px;
  color: #8A8A8A;
  line-height: 1.5;
  max-width: 260px;
}
.my-hubs-empty-btn {
  margin-top: 24px;
  height: 40px;
  padding: 0 24px;
  font-size: 15px;
  font-weight: 600;
}

</style>



// WEBPACK FOOTER //
// src/components/Channels.vue